import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Sprite from './Sprite';
import { useDexControls } from './dexControls';
import { getUsage } from '../services/pokemonService';
import { artworkUrl, formatUsage } from '../utils/dex';
import { usageComparePath } from './UsageSets';

function usageLine(agent) {
  if (typeof agent.usage !== 'number') {
    return agent.ladder ? `No saved usage on the ${agent.ladder} ladder` : 'Usage not in the saved ladder file';
  }
  const pct = formatUsage(agent.usage);
  if (agent.underdog?.below && agent.ladder) return `${pct} on the ${agent.ladder} ladder`;
  return `${pct} usage`;
}

function moveName(move) {
  return typeof move === 'string' ? move : move?.name;
}

function AgentsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback((fresh = false) => {
    setLoading(true);
    setError('');
    getUsage(id, { fresh })
      .then((data) => {
        setUsage(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Smogon moveset data is unavailable right now.');
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    load(false);
  }, [load]);

  useDexControls({
    onB: () => navigate(`/pokemon/${id}`),
  });

  const agents = usage?.agents;
  const entryPath = `/pokemon/${usage?.pokemon?.slug || id}`;

  return (
    <div className="page">
      <div className="toolbar">
        <Link className="btn btn-ghost" to={entryPath}>Back to entry</Link>
      </div>
      <div className="page-intro">
        <div>
          <h1>Agents</h1>
          <p className="lede">
            Surprise sets come first. A lower tier ranks higher, and two or more tiers below is a Sleeper agent. Counters, further down, are the ordinary checks. They are not agents.
          </p>
        </div>
      </div>

      {loading && <p className="status" role="status">Loading agents…</p>}
      {error && (
        <div className="alert" role="alert">
          <p>{error}</p>
          <button className="btn btn-primary" type="button" onClick={() => load(true)}>Retry</button>
        </div>
      )}

      {usage && !loading && (
        <p className="source-line">
          {usage.pokemon?.name} · {usage.tier || 'Unranked'} · {usage.monthLabel || 'latest month'}.
          {' '}Source <a href={usage.chaosUrl || usage.sourceUrl} rel="noreferrer">Smogon</a>.
          {usage.warning ? ` ${usage.warning}` : ''}
        </p>
      )}

      {agents && !agents.available && !loading && !error && (
        <div className="alert" role="status">
          <p>{agents.message || 'Agents are not listed for this Pokémon yet.'}</p>
          {agents.targets?.length > 0 && (
            <ul className="target-links">
              {agents.targets.map((target) => (
                <li key={target.name}>
                  <Link to={`/pokemon/${target.dexSlug || target.dexNumber}/agents`}>
                    {target.name}
                    {typeof target.usage === 'number' ? ` · ${formatUsage(target.usage)}` : ''}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {agents?.available && (
        <section aria-labelledby="agents-heading">
        <h2 id="agents-heading" className="panel-title">Agents</h2>
        {agents.list.length === 0 ? (
          <p>
            {agents.note || 'No surprise set cleared the damage calculator for this Pokémon. Counters below are the ordinary ones.'}
          </p>
        ) : (
          <p>
            Each card is a tech set for this matchup: a move, a spread, or a Speed line. Damage ranges are approximate.
          </p>
        )}
        {agents.list.length > 0 && (
        <ol className="agent-list">
          {agents.list.map((agent) => {
            const path = agent.dexSlug || agent.dexNumber
              ? `/pokemon/${agent.dexSlug || agent.dexNumber}`
              : null;
            return (
              <li className="agent-card" key={agent.name}>
                <div className="agent-head">
                  <Sprite src={agent.sprite} fallback={artworkUrl(agent.dexNumber)} alt="" />
                  <div>
                    <h2>{agent.name}</h2>
                    <div className="agent-badges">
                      <span className="tier-badge">{agent.underdog?.badge || `${agent.tier || 'Unranked'} agent`}</span>
                      {agent.underdog?.label && <span className="sleeper-pill">{agent.underdog.label}</span>}
                    </div>
                    <p>
                      {usageLine(agent)}
                      {agent.underdog?.summary ? ` · ${agent.underdog.summary}` : ''}
                    </p>
                  </div>
                </div>
                <dl className="agent-set">
                  <div>
                    <dt>Ability</dt>
                    <dd>{agent.set.ability || '—'}</dd>
                  </div>
                  <div>
                    <dt>Item</dt>
                    <dd>{agent.set.item || '—'}</dd>
                  </div>
                  <div>
                    <dt>Tera</dt>
                    <dd>{agent.set.tera || '—'}</dd>
                  </div>
                  <div>
                    <dt>Nature and EVs</dt>
                    <dd>
                      {agent.set.nature && agent.set.evText
                        ? `${agent.set.nature} · ${agent.set.evText}`
                        : '—'}
                    </dd>
                  </div>
                  {agent.set.ivText && (
                    <div>
                      <dt>IVs</dt>
                      <dd>{agent.set.ivText}</dd>
                    </div>
                  )}
                </dl>
                <p className="muted">{agent.set.note}</p>
                <h3>Moves</h3>
                <ul className="move-line">
                  {(agent.set.moves || []).map((move) => (
                    <li key={moveName(move)}>{moveName(move)}</li>
                  ))}
                </ul>
                <h3>The trick</h3>
                <p>{agent.trick}</p>
                <h3>Survives</h3>
                <p>{agent.survives}</p>
                <h3>The knockout</h3>
                <p>{agent.knockout}</p>
                <h3>Speed</h3>
                <p>{agent.speed}</p>
                <h3>{agent.underdog?.sleeper ? "Why it's a sleeper" : "Why it's unexpected"}</h3>
                <p>{agent.sleeperWhy}</p>
                <div className="toolbar">
                  {path && <Link className="btn btn-secondary" to={path}>Entry</Link>}
                  <Link className="btn btn-primary" to={usageComparePath(usage.pokemon.name, agent.name)}>
                    Compare
                  </Link>
                </div>
              </li>
            );
          })}
        </ol>
        )}
        </section>
      )}

      {agents?.available && (
        <section aria-labelledby="counters-heading">
          <h2 id="counters-heading" className="panel-title">Counters</h2>
          <p>
            Standard checks. The set is the usual one on that Pokémon’s ladder. These cards are not agents.
          </p>
          <ol className="agent-list">
            {(agents.counters || []).map((counter) => {
              const path = counter.dexSlug || counter.dexNumber
                ? `/pokemon/${counter.dexSlug || counter.dexNumber}`
                : null;
              const pct = typeof counter.usage === 'number' ? formatUsage(counter.usage) : null;
              const where = pct && counter.ladder ? `${pct} on the ${counter.ladder} ladder` : pct;
              return (
                <li className="agent-card counter-card" key={counter.name}>
                  <div className="agent-head">
                    <Sprite src={counter.sprite} fallback={artworkUrl(counter.dexNumber)} alt="" />
                    <div>
                      <h2>{counter.name}</h2>
                      <p>
                        {[counter.tier, where].filter(Boolean).join(' · ') || 'Unranked'}
                      </p>
                    </div>
                  </div>
                  <dl className="agent-set">
                    <div>
                      <dt>Ability</dt>
                      <dd>{counter.set.ability || '—'}</dd>
                    </div>
                    <div>
                      <dt>Item</dt>
                      <dd>{counter.set.item || '—'}</dd>
                    </div>
                    <div>
                      <dt>Tera</dt>
                      <dd>{counter.set.tera || '—'}</dd>
                    </div>
                    <div>
                      <dt>Nature and EVs</dt>
                      <dd>
                        {counter.set.nature && counter.set.evText
                          ? `${counter.set.nature} · ${counter.set.evText}`
                          : '—'}
                      </dd>
                    </div>
                  </dl>
                  <p className="muted">{counter.set.note}</p>
                  <h3>Moves</h3>
                  <ul className="move-line">
                    {(counter.set.moves || []).map((move) => (
                      <li key={moveName(move)}>{moveName(move)}</li>
                    ))}
                  </ul>
                  <h3>Why</h3>
                  <p>{counter.why}</p>
                  <div className="toolbar">
                    {path && <Link className="btn btn-secondary" to={path}>Entry</Link>}
                    <Link className="btn btn-primary" to={usageComparePath(usage.pokemon.name, counter.name)}>
                      Compare
                    </Link>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      )}
    </div>
  );
}

export default AgentsPage;
